import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_API_BASE || '').replace(/\/api$/, '') || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);

export default function WhatsAppAnalyticsPage() {
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` };

  useEffect(() => {
    (async () => {
      try {
        const [o, t] = await Promise.all([
          axios.get(`${API}/api/saas/wa-analytics/overview`, { headers }),
          axios.get(`${API}/api/saas/wa-analytics/timeline?days=30`, { headers })
        ]);
        setData(o.data);
        setTimeline(t.data.dailyMessages || []);
      } catch (err) { console.error(err); }
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, []);

  if (loading) return <div style={{ padding: 40 }}>Loading analytics...</div>;
  if (!data) return <div style={{ padding: 40 }}>Failed to load data</div>;

  // Build daily series for bar chart
  const dateMap = {};
  timeline.forEach(t => {
    const d = t._id.date;
    dateMap[d] = dateMap[d] || { date: d, outbound: 0, inbound: 0 };
    dateMap[d][t._id.direction] = t.count;
  });
  const series = Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  const maxCount = Math.max(1, ...series.map(s => s.outbound + s.inbound));

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 20px' }}>📊 WhatsApp Analytics Dashboard</h2>

      {/* Top KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        <Kpi label="Total Contacts" value={data.contacts.total} sub={`${data.contacts.optedIn} opted-in`} color="#3b82f6" />
        <Kpi label="Campaigns Run" value={data.campaigns.count} sub={`${data.campaigns.sent} messages sent`} color="#8b5cf6" />
        <Kpi label="Delivery Rate" value={`${data.campaigns.deliveryRate}%`} sub={`${data.campaigns.delivered} delivered`} color="#10b981" />
        <Kpi label="Read Rate" value={`${data.campaigns.readRate}%`} sub={`${data.campaigns.read} read`} color="#06b6d4" />
        <Kpi label="Reply Rate" value={data.campaigns.delivered ? ((data.campaigns.replied / data.campaigns.delivered) * 100).toFixed(1) + '%' : '0%'} sub={`${data.campaigns.replied} replies`} color="#f59e0b" />
        <Kpi label="Failed" value={data.campaigns.failed} sub="needs retry" color="#ef4444" />
      </div>

      {/* Message activity */}
      <div style={{ background: 'white', borderRadius: 8, padding: 16, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ margin: '0 0 12px' }}>💬 Message Activity (last 30 days)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
          <div><span style={{ fontSize: 24, fontWeight: 700 }}>{data.messages.sent30d}</span><div style={{ fontSize: 12, color: '#666' }}>Sent</div></div>
          <div><span style={{ fontSize: 24, fontWeight: 700 }}>{data.messages.received30d}</span><div style={{ fontSize: 12, color: '#666' }}>Received</div></div>
          <div><span style={{ fontSize: 24, fontWeight: 700 }}>{data.messages.activeConversations}</span><div style={{ fontSize: 12, color: '#666' }}>Active chats</div></div>
        </div>

        {/* Simple bar chart */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 120, borderBottom: '1px solid #e5e7eb', padding: '0 4px' }}>
          {series.map(s => {
            const total = s.outbound + s.inbound;
            const h = (total / maxCount) * 100;
            return (
              <div key={s.date} title={`${s.date}: ↑${s.outbound} ↓${s.inbound}`}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}>
                <div style={{ width: '80%', display: 'flex', flexDirection: 'column-reverse' }}>
                  <div style={{ height: `${(s.outbound / maxCount) * 120}px`, background: '#25D366', borderRadius: '2px 2px 0 0' }} />
                  <div style={{ height: `${(s.inbound / maxCount) * 120}px`, background: '#3b82f6' }} />
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: '#666', display: 'flex', gap: 16, marginTop: 6 }}>
          <span><span style={{ background: '#25D366', display: 'inline-block', width: 10, height: 10 }}/> Outbound</span>
          <span><span style={{ background: '#3b82f6', display: 'inline-block', width: 10, height: 10 }}/> Inbound</span>
        </div>
      </div>

      {/* Automation + Leads */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={{ background: 'white', borderRadius: 8, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 12px' }}>🤖 Automation</h3>
          <Row k="Auto-reply rules" v={data.automation.autoReplyRules} />
          <Row k="Enabled rules" v={data.automation.enabledRules} />
          <Row k="Total triggers" v={data.automation.totalTriggers} />
          <Row k="Active chatbot flows" v={data.automation.activeFlows} />
        </div>
        <div style={{ background: 'white', borderRadius: 8, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 12px' }}>📝 Lead Capture</h3>
          <Row k="Published forms" v={data.leads.forms} />
          <Row k="Total submissions" v={data.leads.totalSubmissions} />
          <Row k="Last 30 days" v={data.leads.last30d} />
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, color }) {
  return (
    <div style={{ background: 'white', padding: 16, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `3px solid ${color}` }}>
      <div style={{ fontSize: 12, color: '#666' }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 11, color: '#888' }}>{sub}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 14 }}>
      <span style={{ color: '#666' }}>{k}</span>
      <strong>{v}</strong>
    </div>
  );
}
